import {
  IGetListYearGroupService,
  YearGroup,
} from "../../../../abstractions/services/actions/IGetListYearGroupService";
import { BaseItemResponse } from "../../../../base/response/BaseItemResponse";
import { createCachedFactory } from "../../../../utils/factoryUtils";
import { agruparPorSemana, SemanaMonitoreo } from "../agruparPorSemana";
import { fx_createSesionDashboardGrpcService } from "./SesionDashboardGrpcService";

/** Una semana de monitoreo como item del sidebar: la `SemanaMonitoreo` ya
 *  agrupada, el `id`/`isEliminado` que exige `BaseSecondSidebarRender`, y el
 *  nombre de cada lote.
 *
 *  Los nombres viajan con el item —la misma referencia compartida por todas
 *  las semanas— porque `datos` está indexado por `loteId` y el sidebar tiene
 *  que mostrar nombres. Vienen del mismo endpoint del dashboard que ya se
 *  pide acá, así que resolverlos aparte significaría una segunda carga. */
export type SemanaMonitoreoItem = BaseItemResponse &
  SemanaMonitoreo & {
    /** `loteId` → nombre, de todos los lotes del histórico. */
    loteNombres: Record<string, string>;
  };

/** Semanas de monitoreo de una finca, agrupadas por año, para el sidebar de la
 *  vista de sesión.
 *
 *  No pide nada nuevo al servidor: se apoya en el mismo endpoint del dashboard
 *  que ya alimenta al Análisis —que trae el histórico entero con las
 *  incidencias por lote y plaga— y lo pasa por `agruparPorSemana`. Por eso el
 *  mapa no necesita el endpoint pesado `…/list` (árboles, GPS y detecciones
 *  crudas): para pintar el semáforo de un lote alcanza con su `inc`.
 *
 *  No extiende `BaseGetYearGroupListService` porque esa base agrupa 1 item =
 *  1 fila por fecha; acá cada fila es una semana que agrupa varios recorridos.
 *  Mismo motivo por el que `MonitoreoBrocaGetSemanaGroupListByFincaIdService`
 *  tampoco la extiende. */
class SesionSemanaGroupListByFincaIdService
  implements IGetListYearGroupService<SemanaMonitoreoItem>
{
  constructor(private readonly _fincaId: string) {}

  async getListAsync(): Promise<YearGroup<SemanaMonitoreoItem>[]> {
    const { lotes, sesiones } = await fx_createSesionDashboardGrpcService(
      this._fincaId,
    ).getAsync();

    // Una sola referencia compartida por todas las semanas: el catálogo de
    // lotes es el mismo para el histórico entero.
    const loteNombres = Object.fromEntries(
      lotes.map((lote) => [lote.id, lote.nombre]),
    );

    const semanas = agruparPorSemana(sesiones);
    const porAnio = new Map<number, SemanaMonitoreoItem[]>();

    for (const semana of semanas) {
      if (!porAnio.has(semana.anio)) porAnio.set(semana.anio, []);
      porAnio.get(semana.anio)!.push({
        // El id identifica la SEMANA, no una sesión: varias salidas de la
        // misma semana son una sola fila del sidebar.
        id: `${semana.anio}-${semana.semana}`,
        isEliminado: false,
        loteNombres,
        ...semana,
      });
    }

    // Años y semanas descendente: lo más reciente primero, que es por donde se
    // empieza a mirar.
    return [...porAnio.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([year, items]) => ({
        year,
        items: [...items].sort((a, b) => b.semana - a.semana),
      }));
  }
}

/** Factory cacheada: una única instancia por fincaId. */
export const fx_createSesionSemanaGroupListByFincaIdService =
  createCachedFactory(
    (fincaId) => new SesionSemanaGroupListByFincaIdService(fincaId),
  );
